import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { UserCheck, Loader2, RotateCw } from 'lucide-react';
import { publicApi } from '../lib/api';
import { contactInfo } from '../data/mock';
import { useToast } from '../hooks/use-toast';

const MyBookings = () => {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  const lookup = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const r = await publicApi.get('/bookings/by-email', { params: { email } });
      setRows(r.data || []);
      if ((r.data || []).length === 0) toast({ title: 'No past bookings found for this email' });
    } catch {
      toast({ title: 'Lookup failed', description: 'Please try again shortly.' });
    } finally {
      setLoading(false);
    }
  };

  const rebook = (row) => {
    const msg = encodeURIComponent(
      `Hi Dawn! I'd like to rebook: ${row.treatment || 'my usual treatment'}. Could you confirm the next available slot? Thank you!`
    );
    window.open(`https://wa.me/${contactInfo.whatsappIntl}?text=${msg}`, '_blank');
  };

  return (
    <section id="my-bookings" className="relative py-20 md:py-28">
      <div className="max-w-3xl mx-auto px-6 md:px-10">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="text-center mb-10">
          <p className="text-white/80 tracking-[0.3em] text-xs md:text-sm font-light mb-4" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>
            RETURNING CLIENT
          </p>
          <h2 className="text-white font-light" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(2rem, 4.5vw, 3.5rem)' }}>
            Rebook a favourite treatment
          </h2>
          <p className="text-white/90 mt-4 italic font-light text-lg" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Enter the email you used before to see your past treatments.
          </p>
          <div className="mt-6 mx-auto w-24 h-px bg-white/50" />
        </motion.div>

        <motion.form
          onSubmit={lookup}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.8 }}
          className="bg-white/20 backdrop-blur-md rounded-3xl border border-white/30 p-6 md:p-8 shadow-2xl"
        >
          <div className="flex flex-col sm:flex-row items-stretch gap-3">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="flex-1 bg-transparent border-b border-white/40 focus:border-white text-white placeholder-white/50 py-3 outline-none"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.1rem' }}
            />
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-white/70 text-white text-xs tracking-[0.25em] bg-white/5 hover:bg-white hover:text-rose-500 transition-all duration-500 disabled:opacity-60"
              style={{ fontFamily: "'Josefin Sans', sans-serif" }}
            >
              {loading ? (<><Loader2 size={14} className="animate-spin" /> LOOKING</>) : (<><UserCheck size={14} /> FIND MY BOOKINGS</>)}
            </button>
          </div>

          {rows && rows.length > 0 && (
            <div className="mt-6 space-y-3">
              {rows.map((r) => (
                <div
                  key={r.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white/10 border border-white/20 rounded-2xl px-5 py-4"
                >
                  <div className="text-white">
                    <p className="tracking-[0.15em] text-xs opacity-80" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>
                      {r.date || new Date(r.created_at).toLocaleDateString('en-ZA')}
                      {r.time && ` @ ${r.time}`}
                    </p>
                    <p className="mt-1" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.15rem' }}>
                      {r.treatment || 'Treatment not specified'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => rebook(r)}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/70 text-white text-xs tracking-[0.25em] bg-white/10 hover:bg-white hover:text-rose-500 transition-all"
                    style={{ fontFamily: "'Josefin Sans', sans-serif" }}
                  >
                    <RotateCw size={13} /> REBOOK
                  </button>
                </div>
              ))}
            </div>
          )}
          {rows && rows.length === 0 && (
            <p className="mt-6 text-center text-white/85" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.05rem' }}>
              No past bookings found for this email yet. Book below to get started!
            </p>
          )}
        </motion.form>
      </div>
    </section>
  );
};

export default MyBookings;
